<section *ngIf="panelVisible" class="customer-integration-panel" data-testid="customer-integration-panel">
    <header class="customer-integration-panel__header" *ngIf="!hidePanelHeading">
        <div>
            <h2 class="customer-integration-panel__title">Customer Integration</h2>
            <p class="customer-integration-panel__intro">
                Shared outbound webhook configuration for this hub in the current KYRA environment.
            </p>
        </div>
        <div class="customer-integration-panel__env" *ngIf="environment">
            <span class="env-label">Current Environment</span>
            <span class="env-badge">{{ formatEnvironmentLabel(environment) }}</span>
        </div>
    </header>
    <div class="customer-integration-panel__env customer-integration-panel__env--standalone"
         *ngIf="hidePanelHeading && environment">
        <span class="env-label">Current Environment</span>
        <span class="env-badge">{{ formatEnvironmentLabel(environment) }}</span>
    </div>

    <div *ngIf="loading" class="customer-integration-panel__loading">Loading customer integration…</div>

    <ng-container *ngIf="!loading">
        <section class="delivery-history-section" data-testid="delivery-history-section">
            <header class="delivery-history-section__header">
                <div>
                    <h3 class="delivery-history-section__title">Delivery History</h3>
                    <p class="helper-text">
                        Publishing decisions and outbound delivery outcomes for this Organization.
                        Intentional skips are not treated as delivery failures.
                        Date filters use UTC calendar days.
                    </p>
                </div>
            </header>

            <div class="history-filters" data-testid="delivery-history-filters">
                <label>
                    <span>From</span>
                    <input type="date" [(ngModel)]="historyFilters.created_after" name="historyFrom">
                </label>
                <label>
                    <span>To</span>
                    <input type="date" [(ngModel)]="historyFilters.created_before" name="historyTo">
                </label>
                <label>
                    <span>Publishing outcome</span>
                    <select [(ngModel)]="historyFilters.outcome" name="historyOutcome">
                        <option value="">All</option>
                        <option value="SKIPPED">Skipped</option>
                        <option value="OUTBOX_CREATED">Outbox created</option>
                        <option value="OUTBOX_EXISTING">Outbox existing</option>
                    </select>
                </label>
                <label>
                    <span>Delivery status</span>
                    <select [(ngModel)]="historyFilters.delivery_status" name="historyDeliveryStatus">
                        <option value="">All</option>
                        <option value="PENDING">Pending</option>
                        <option value="PROCESSING">Processing</option>
                        <option value="RETRYING">Retrying</option>
                        <option value="DELIVERED">Delivered</option>
                        <option value="FAILED">Failed</option>
                    </select>
                </label>
                <label class="history-filters__event">
                    <span>Event ID</span>
                    <input type="text"
                           [(ngModel)]="historyFilters.event_id"
                           name="historyEventId"
                           placeholder="Search event id"
                           maxlength="256">
                </label>
                <div class="history-filters__actions">
                    <button type="button" class="btn-save" (click)="applyHistoryFilters()" data-testid="history-apply-filters">
                        Apply
                    </button>
                    <button type="button" class="btn-cancel" (click)="clearHistoryFilters()" data-testid="history-clear-filters">
                        Clear
                    </button>
                </div>
            </div>

            <div *ngIf="historyLoading" class="customer-integration-panel__loading" data-testid="delivery-history-loading">
                Loading delivery history…
            </div>

            <div *ngIf="!historyLoading && historyRows.length === 0"
                 class="history-empty"
                 data-testid="delivery-history-empty">
                No delivery or publishing decisions recorded for this Organization yet.
            </div>

            <div class="responsive-table-wrapper" *ngIf="!historyLoading && historyRows.length > 0">
                <table data-testid="delivery-history-table">
                    <thead>
                    <tr>
                        <th></th>
                        <th>When</th>
                        <th>Event ID</th>
                        <th>Classification</th>
                        <th>Publishing reason</th>
                        <th>Deliveries</th>
                    </tr>
                    </thead>
                    <tbody>
                    <ng-container *ngFor="let row of historyRows">
                        <tr class="history-row" [attr.data-testid]="'history-row-' + row.vehicle_log_id">
                            <td>
                                <button type="button"
                                        class="history-expand-btn"
                                        (click)="toggleHistoryRow(row)"
                                        [attr.aria-expanded]="!!row._expanded"
                                        [attr.data-testid]="'history-expand-' + row.vehicle_log_id">
                                    <fa-icon [icon]="['fas', row._expanded ? 'chevron-down' : 'chevron-right']"></fa-icon>
                                </button>
                            </td>
                            <td>{{ row.history_at ? (row.history_at | date:'dd MMM yyyy HH:mm') : '—' }}</td>
                            <td><span class="mono">{{ row.event_id || '—' }}</span></td>
                            <td>
                                <span class="history-pill" [ngClass]="classificationClass(row.classification)">
                                    {{ classificationLabel(row.classification) }}
                                </span>
                            </td>
                            <td>
                                <ng-container *ngIf="row.decision; else noDecision">
                                    {{ row.decision.reason_label || row.decision.reason_code || '—' }}
                                </ng-container>
                                <ng-template #noDecision>Delivery only (no decision record)</ng-template>
                            </td>
                            <td>
                                <ng-container *ngIf="row.deliveries?.length; else noDeliveries">
                                    <span *ngFor="let delivery of row.deliveries"
                                          class="history-pill history-pill--compact"
                                          [ngClass]="deliveryStatusClass(delivery.status)">
                                        {{ delivery.status }}
                                    </span>
                                </ng-container>
                                <ng-template #noDeliveries>
                                    <span class="helper-text">None</span>
                                </ng-template>
                            </td>
                        </tr>
                        <tr *ngIf="row._expanded" class="history-detail-row" [attr.data-testid]="'history-detail-' + row.vehicle_log_id">
                            <td colspan="6">
                                <div class="history-detail">
                                    <div class="history-detail__block">
                                        <h5>Movement</h5>
                                        <p>
                                            Plate {{ row.vehicle_summary?.vehicle_number || '—' }}
                                            · {{ row.vehicle_summary?.direction || '—' }}
                                            · Camera {{ row.vehicle_summary?.camera_esn || '—' }}
                                            · VL #{{ row.vehicle_log_id }}
                                        </p>
                                    </div>
                                    <div class="history-detail__block" *ngIf="row.decision as decision">
                                        <h5>Publishing decision</h5>
                                        <p>
                                            {{ decision.outcome_label }}
                                            ({{ decision.outcome }})
                                            · {{ decision.reason_label || decision.reason_code }}
                                        </p>
                                        <p class="helper-text" *ngIf="decision.reason_detail">
                                            {{ decision.reason_detail }}
                                        </p>
                                    </div>
                                    <div class="history-detail__block" *ngIf="!row.decision">
                                        <h5>Publishing decision</h5>
                                        <p class="helper-text">
                                            No durable decision (pre-feature or compatibility path).
                                            Delivery status below remains authoritative.
                                        </p>
                                    </div>
                                    <div class="history-detail__block">
                                        <h5>Deliveries</h5>
                                        <div *ngIf="!row.deliveries?.length" class="helper-text">
                                            No outbound deliveries (intentional skip or not eligible).
                                        </div>
                                        <table *ngIf="row.deliveries?.length" class="history-delivery-table">
                                            <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Integration</th>
                                                <th>Status</th>
                                                <th>Attempts</th>
                                                <th>Last attempt</th>
                                                <th>Delivered</th>
                                                <th>Error</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr *ngFor="let delivery of row.deliveries">
                                                <td>{{ delivery.id }}</td>
                                                <td>{{ delivery.integration_name || ('#' + delivery.integration_id) }}</td>
                                                <td>
                                                    <span class="history-pill history-pill--compact"
                                                          [ngClass]="deliveryStatusClass(delivery.status)">
                                                        {{ delivery.status }}
                                                    </span>
                                                </td>
                                                <td>{{ delivery.attempt_count }}</td>
                                                <td>{{ delivery.last_attempt_at ? (delivery.last_attempt_at | date:'dd MMM yyyy HH:mm') : '—' }}</td>
                                                <td>{{ delivery.delivered_at ? (delivery.delivered_at | date:'dd MMM yyyy HH:mm') : '—' }}</td>
                                                <td>{{ delivery.last_error || '—' }}</td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </ng-container>
                    </tbody>
                </table>
            </div>

            <app-vte-pagination
                    *ngIf="historyTotalRecords > 0"
                    [currentPage]="historyPage"
                    [pageSize]="historyPageSize"
                    [totalRecords]="historyTotalRecords"
                    [showJumpToPage]="true"
                    (pageChange)="onHistoryPageChange($event)"
                    (pageSizeChange)="onHistoryPageSizeChange($event)">
            </app-vte-pagination>
        </section>

        <section class="configuration-accordion" data-testid="configuration-accordion">
            <button type="button"
                    class="configuration-accordion__toggle"
                    (click)="toggleConfiguration()"
                    [attr.aria-expanded]="configurationExpanded"
                    data-testid="configuration-toggle">
                <fa-icon [icon]="['fas', configurationExpanded ? 'chevron-down' : 'chevron-right']"></fa-icon>
                <span>Integration configuration</span>
                <span class="helper-text">Webhook URL, HMAC, API keys, and setup checklist</span>
            </button>

            <div *ngIf="configurationExpanded" class="configuration-accordion__body" data-testid="configuration-body">
        <!-- Empty state -->
        <div *ngIf="!integration" class="empty-state" data-testid="customer-integration-empty">
            <p>No customer integration is configured for this Organization.</p>
            <div class="button-row">
                <button class="btn-save" type="button" (click)="openCreateModal()" data-testid="create-integration-btn">
                    <fa-icon [icon]="['fas', 'square-plus']" style="padding-right: 3px;"></fa-icon>
                    Create New Integration
                </button>
                <button class="btn-cancel" type="button" (click)="openLinkModal()" data-testid="link-integration-btn">
                    <fa-icon [icon]="['fas', 'link']" style="padding-right: 3px;"></fa-icon>
                    Link Existing Integration
                </button>
            </div>
        </div>

        <!-- Configured state -->
        <div *ngIf="integration" class="configured-state" data-testid="customer-integration-configured">
            <app-operational-warning-card
                    *ngIf="sharedHubsWarning as warning"
                    [warning]="warning">
            </app-operational-warning-card>

            <div class="detail-grid">
                <div class="detail-item">
                    <span class="detail-label">Environment</span>
                    <span class="detail-value">{{ formatEnvironmentLabel(integration.environment) }}</span>
                </div>
                <div class="detail-item">
                    <span class="detail-label">Integration Name</span>
                    <span class="detail-value">{{ integration.name }}</span>
                </div>
                <div class="detail-item detail-item--wide">
                    <span class="detail-label">Webhook URL</span>
                    <span class="detail-value mono">{{ integration.webhook_url }}</span>
                </div>
                <div class="detail-item">
                    <span class="detail-label">HMAC Secret</span>
                    <span class="detail-value mono" data-testid="hmac-masked">
                        {{ integration.hmac_secret_masked || '••••••••••••' }}
                    </span>
                </div>
                <div class="detail-item">
                    <span class="detail-label">Status</span>
                    <span class="status-pill"
                          [class.active]="integration.is_active"
                          [class.inactive]="!integration.is_active">
                        {{ integration.is_active ? 'Active' : 'Inactive' }}
                    </span>
                </div>
            </div>

            <div class="section-actions">
                <button class="btn-save" type="button" (click)="openEditModal()" data-testid="edit-integration-btn">
                    <fa-icon [icon]="['fas', 'pen']" style="padding-right: 3px;"></fa-icon>
                    Edit
                </button>
                <button class="btn-save" type="button" (click)="openGenerateHmacConfirm()"
                        [disabled]="saving" data-testid="generate-hmac-btn">
                    Generate New HMAC Secret
                </button>
                <button class="btn-cancel" type="button" (click)="openUnlinkConfirm()" data-testid="unlink-integration-btn">
                    <fa-icon [icon]="['fas', 'unlink']" style="padding-right: 3px;"></fa-icon>
                    Unlink This Hub
                </button>
            </div>

            <section class="checklist-section" data-testid="setup-checklist">
                <h4>Setup Checklist</h4>
                <p class="helper-text">
                    Derived from live configuration and delivery records. Saving a URL or secret does not mark webhooks as verified.
                </p>
                <div *ngIf="statusLoading" class="customer-integration-panel__loading">Loading checklist…</div>
                <ul *ngIf="!statusLoading" class="checklist">
                    <li *ngFor="let item of checklist" class="checklist-item">
                        <span class="checklist-status" [ngClass]="checklistStatusClass(item.status)">
                            {{ checklistStatusLabel(item.status) }}
                        </span>
                        <span class="checklist-label">{{ item.label }}</span>
                        <span class="checklist-detail">{{ item.detail }}</span>
                    </li>
                    <li *ngIf="!checklist.length" class="hub-empty">Checklist unavailable.</li>
                </ul>
            </section>

            <section class="activity-section" data-testid="activity-timeline">
                <h4>Activity</h4>
                <p class="helper-text">
                    Lightweight status from integration metadata and outbound delivery records (not a full audit log).
                </p>
                <div class="summary-row" *ngIf="statusSummary as summary">
                    <span>Last attempt: {{ summary.last_attempt_at ? (summary.last_attempt_at | date:'dd MMM yyyy HH:mm') : '—' }}</span>
                    <span>Last success: {{ summary.last_delivered_at ? (summary.last_delivered_at | date:'dd MMM yyyy HH:mm') : '—' }}</span>
                    <span>Last failure: {{ summary.last_failure_at ? (summary.last_failure_at | date:'dd MMM yyyy HH:mm') : '—' }}</span>
                </div>
                <p class="helper-text" *ngIf="statusSummary?.last_error">
                    Last failure detail: {{ statusSummary?.last_error }}
                </p>
                <ul class="activity-list">
                    <li *ngFor="let item of activity" class="activity-item">
                        <span class="activity-when">{{ item.at ? (item.at | date:'dd MMM yyyy HH:mm') : '—' }}</span>
                        <span class="activity-status">{{ item.status }}</span>
                        <span class="activity-detail">{{ item.detail }}</span>
                    </li>
                    <li *ngIf="!activity.length" class="hub-empty">No activity recorded yet.</li>
                </ul>
            </section>

            <section class="hubs-section">
                <h4>Authorized Hubs</h4>
                <p class="helper-text">
                    Webhook URL, HMAC secret, and API keys belong to this shared integration.
                </p>
                <ul class="hub-list" data-testid="authorized-hubs">
                    <li *ngFor="let hub of authorizedHubRows"
                        [class.current-hub]="hub.isCurrent">
                        <span class="hub-check">✓</span>
                        <span>{{ hub.name }}</span>
                        <span *ngIf="hub.isCurrent" class="current-tag">Current</span>
                    </li>
                    <li *ngIf="!authorizedHubRows.length" class="hub-empty">No authorized hubs.</li>
                </ul>
            </section>

            <section class="credentials-section">
                <div class="credentials-header">
                    <h4>API Keys</h4>
                    <button class="btn-save" type="button" (click)="issueApiKey()" [disabled]="saving"
                            data-testid="issue-api-key-btn">
                        <fa-icon [icon]="['fas', 'square-plus']" style="padding-right: 3px;"></fa-icon>
                        Issue API Key
                    </button>
                </div>
                <div *ngIf="keysLoading" class="customer-integration-panel__loading">Loading API keys…</div>
                <div class="responsive-table-wrapper" *ngIf="!keysLoading">
                    <table>
                        <thead>
                        <tr>
                            <th>Masked Key</th>
                            <th>Status</th>
                            <th>Last Used</th>
                            <th>Created</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr *ngFor="let key of apiKeys">
                            <td><span class="credential-badge">{{ key.masked_key }}</span></td>
                            <td>
                                <span class="status-pill"
                                      [class.active]="key.is_active"
                                      [class.inactive]="!key.is_active">
                                    {{ key.is_active ? 'Active' : 'Inactive' }}
                                </span>
                            </td>
                            <td>{{ key.last_used_at ? (key.last_used_at | date:'dd MMM yyyy') : '—' }}</td>
                            <td>{{ key.created_at | date:'dd MMM yyyy' }}</td>
                            <td>
                                <label class="toggle-row">
                                    <input type="checkbox"
                                           [checked]="key.is_active"
                                           [disabled]="key._toggling"
                                           (change)="toggleApiKey(key, $event)">
                                    <span>{{ key.is_active ? 'Disable' : 'Enable' }}</span>
                                </label>
                            </td>
                        </tr>
                        <tr *ngIf="apiKeys.length === 0">
                            <td colspan="5">No API keys issued yet.</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
            </div>
        </section>
    </ng-container>
</section>

<!-- Create modal -->
<div *ngIf="showCreateModal" class="confirmation-modal" data-testid="create-modal">
    <div class="modal-content customer-integration-modal">
        <header class="top-header modal-header customer-integration-modal__header">
            <h3>Create New Integration</h3>
            <p *ngIf="environment" class="customer-integration-modal__env-note">
                Environment will be {{ formatEnvironmentLabel(environment) }} (server-assigned).
            </p>
        </header>
        <form [formGroup]="createForm" (ngSubmit)="submitCreate()" class="modal-form">
            <div class="form-group">
                <label for="create-integration-name">Integration Name <small>*</small></label>
                <input id="create-integration-name" type="text" formControlName="name" maxlength="64"
                       autocomplete="off">
            </div>
            <div class="form-group">
                <label for="create-webhook-url">Webhook URL <small>*</small></label>
                <input id="create-webhook-url" type="url" formControlName="webhook_url" maxlength="512"
                       autocomplete="off">
            </div>
            <div class="form-group hmac-source-group">
                <span id="hmac-secret-source-label" class="hmac-source-label">HMAC Secret Source</span>
                <div class="hmac-source-segmented"
                     role="radiogroup"
                     aria-labelledby="hmac-secret-source-label"
                     (keydown)="onHmacSourceKeydown($event)"
                     data-testid="create-hmac-mode">
                    <button type="button"
                            role="radio"
                            class="hmac-source-option"
                            [class.is-selected]="createHmacMode === 'customer'"
                            [attr.aria-checked]="createHmacMode === 'customer'"
                            (click)="setCreateHmacMode('customer')"
                            data-testid="hmac-source-customer">
                        <fa-icon [icon]="['fas', 'key']" aria-hidden="true"></fa-icon>
                        <span>Customer-provided</span>
                    </button>
                    <button type="button"
                            role="radio"
                            class="hmac-source-option"
                            [class.is-selected]="createHmacMode === 'generate'"
                            [attr.aria-checked]="createHmacMode === 'generate'"
                            (click)="setCreateHmacMode('generate')"
                            data-testid="hmac-source-generate">
                        <fa-icon [icon]="['fas', 'shield-halved']" aria-hidden="true"></fa-icon>
                        <span>Generate with KYRA</span>
                    </button>
                </div>
            </div>
            <div class="form-group hmac-secret-field" *ngIf="createHmacMode === 'customer'">
                <label for="create-hmac-secret">HMAC Secret <small>*</small></label>
                <input id="create-hmac-secret" type="password" formControlName="hmac_secret"
                       autocomplete="new-password" data-testid="create-hmac-input">
            </div>
            <p class="helper-text hmac-generate-hint" *ngIf="createHmacMode === 'generate'"
               data-testid="create-hmac-generate-hint">
                KYRA will generate the secret and show it once for secure sharing.
            </p>
            <div class="form-group checkbox-row">
                <label>
                    <input type="checkbox" formControlName="is_active">
                    Active
                </label>
            </div>
            <div class="button-row customer-integration-modal__actions">
                <button class="btn-save" type="submit" [disabled]="saving"
                        data-testid="create-submit-btn">
                    {{ saving ? 'Creating…' : 'Create' }}
                </button>
                <button class="btn-cancel" type="button" (click)="closeCreateModal()" [disabled]="saving">Cancel</button>
            </div>
        </form>
    </div>
</div>

<!-- Link modal -->
<div *ngIf="showLinkModal" class="confirmation-modal" data-testid="link-modal">
    <div class="modal-content customer-integration-modal">
        <header class="top-header modal-header">
            <h3>Link Existing Integration</h3>
            <p *ngIf="environment">Current Environment: {{ formatEnvironmentLabel(environment) }}</p>
        </header>
        <div *ngIf="linkLoading" class="customer-integration-panel__loading">Loading available integrations…</div>
        <div *ngIf="!linkLoading">
            <p class="helper-text">Available Customer Integrations</p>
            <div class="link-options" data-testid="link-options">
                <label *ngFor="let option of linkOptions" class="link-option">
                    <input type="radio"
                           name="linkIntegration"
                           [value]="option.id"
                           [checked]="selectedLinkIntegrationId === option.id"
                           (change)="selectedLinkIntegrationId = option.id">
                    <span>{{ option.name }}</span>
                </label>
                <p *ngIf="!linkOptions.length" class="hub-empty">No integrations available in the current environment.</p>
            </div>
            <div class="button-row">
                <button class="btn-save" type="button" (click)="submitLink()"
                        [disabled]="saving || !selectedLinkIntegrationId">Link</button>
                <button class="btn-cancel" type="button" (click)="closeLinkModal()" [disabled]="saving">Cancel</button>
            </div>
        </div>
    </div>
</div>

<!-- Edit modal -->
<div *ngIf="showEditModal" class="confirmation-modal" data-testid="edit-modal">
    <div class="modal-content customer-integration-modal">
        <header class="top-header modal-header">
            <h3>Edit Integration</h3>
            <p>Environment is display-only and cannot be changed.</p>
        </header>
        <form [formGroup]="editForm" (ngSubmit)="submitEdit()" class="modal-form">
            <div class="form-group">
                <label>Environment</label>
                <input type="text" [value]="formatEnvironmentLabel(integration?.environment)" readonly disabled>
            </div>
            <div class="form-group">
                <label>Integration Name <small>*</small></label>
                <input type="text" formControlName="name" maxlength="64" autocomplete="off">
            </div>
            <div class="form-group">
                <label>Webhook URL <small>*</small></label>
                <input type="url" formControlName="webhook_url" maxlength="512" autocomplete="off">
            </div>
            <div class="form-group">
                <label>Current HMAC Secret</label>
                <input type="text" [value]="integration?.hmac_secret_masked || '••••••••••••'" readonly disabled>
            </div>
            <div class="form-group">
                <label>New HMAC Secret</label>
                <input type="password" formControlName="hmac_secret" autocomplete="new-password"
                       placeholder="Leave blank to keep existing secret"
                       data-testid="edit-hmac-input">
                <small class="helper-text">Leave empty to preserve the existing secret.</small>
            </div>
            <div class="form-group checkbox-row">
                <label>
                    <input type="checkbox" formControlName="is_active">
                    Active
                </label>
            </div>
            <div class="button-row">
                <button class="btn-save" type="submit" [disabled]="saving">Save</button>
                <button class="btn-cancel" type="button" (click)="closeEditModal()" [disabled]="saving">Cancel</button>
            </div>
        </form>
    </div>
</div>

<!-- Unlink confirm -->
<div *ngIf="showUnlinkConfirm" class="confirmation-modal" data-testid="unlink-modal">
    <div class="modal-content customer-integration-modal">
        <header class="top-header modal-header">
            <h3>Unlink This Hub</h3>
            <p>
                This removes only the membership for this Organization.
                The shared integration, API keys, and other hubs remain unchanged.
            </p>
        </header>
        <div class="button-row">
            <button class="btn-cancel" type="button" (click)="confirmUnlink()" [disabled]="saving"
                    data-testid="confirm-unlink-btn">Unlink</button>
            <button class="btn-save" type="button" (click)="closeUnlinkConfirm()" [disabled]="saving">Cancel</button>
        </div>
    </div>
</div>

<!-- One-time raw API key -->
<div *ngIf="showRawKeyModal" class="confirmation-modal" data-testid="raw-key-modal">
    <div class="modal-content success-modal-content">
        <header class="top-header modal-header">
            <h3>API Key Created</h3>
            <p>This key will not be shown again. Copy it now and share it securely with the customer.</p>
        </header>
        <div class="generated-key-box" data-testid="raw-key-value">{{ generatedRawKey }}</div>
        <div class="button-row">
            <app-copy-button [value]="generatedRawKey" copyTitle="Copy API key"></app-copy-button>
            <button class="btn-cancel" type="button" (click)="closeRawKeyModal()" data-testid="close-raw-key-btn">
                <fa-icon [icon]="['fas', 'square-xmark']" style="padding-right: 3px;"></fa-icon>
                Done
            </button>
        </div>
    </div>
</div>

<!-- Confirm KYRA HMAC generation -->
<div *ngIf="showGenerateHmacConfirm" class="confirmation-modal" data-testid="generate-hmac-confirm">
    <div class="modal-content customer-integration-modal">
        <header class="top-header modal-header">
            <h3>Generate New HMAC Secret</h3>
            <p>
                This replaces the current HMAC secret. The previous secret stops working for webhook verification
                once the customer switches. The new value is shown once.
            </p>
        </header>
        <div class="button-row">
            <button class="btn-save" type="button" (click)="confirmGenerateHmac()" [disabled]="saving"
                    data-testid="confirm-generate-hmac-btn">Generate</button>
            <button class="btn-cancel" type="button" (click)="closeGenerateHmacConfirm()" [disabled]="saving">Cancel</button>
        </div>
    </div>
</div>

<!-- Confirm customer-provided HMAC rotation on edit -->
<div *ngIf="showRotateHmacConfirm" class="confirmation-modal" data-testid="rotate-hmac-confirm">
    <div class="modal-content customer-integration-modal">
        <header class="top-header modal-header">
            <h3>Replace HMAC Secret</h3>
            <p>Confirm you intend to replace the existing HMAC secret with the value you entered.</p>
        </header>
        <div class="button-row">
            <button class="btn-save" type="button" (click)="confirmRotateHmac()" [disabled]="saving"
                    data-testid="confirm-rotate-hmac-btn">Replace Secret</button>
            <button class="btn-cancel" type="button" (click)="cancelRotateHmac()" [disabled]="saving">Cancel</button>
        </div>
    </div>
</div>

<!-- One-time generated HMAC -->
<div *ngIf="showGeneratedHmacModal" class="confirmation-modal" data-testid="generated-hmac-modal">
    <div class="modal-content success-modal-content">
        <header class="top-header modal-header">
            <h3>HMAC Secret Generated</h3>
            <p>
                Copy this secret now and share it securely with the customer.
                KYRA will not show it again. Storing it does not prove the customer receiver accepted it.
            </p>
        </header>
        <div class="generated-key-box" data-testid="generated-hmac-value">{{ generatedHmacSecret }}</div>
        <div class="button-row">
            <app-copy-button [value]="generatedHmacSecret" copyTitle="Copy HMAC secret"></app-copy-button>
            <button class="btn-cancel" type="button" (click)="closeGeneratedHmacModal()" data-testid="close-generated-hmac-btn">
                Done
            </button>
        </div>
    </div>
</div>

<!-- Confirm API key revoke -->
<div *ngIf="showRevokeKeyConfirm" class="confirmation-modal" data-testid="revoke-key-confirm">
    <div class="modal-content customer-integration-modal">
        <header class="top-header modal-header">
            <h3>Disable API Key</h3>
            <p>
                Disable {{ pendingRevokeKey?.masked_key }}?
                History API calls using this key will be rejected until it is enabled again.
            </p>
        </header>
        <div class="button-row">
            <button class="btn-cancel" type="button" (click)="confirmRevokeKey()" [disabled]="saving"
                    data-testid="confirm-revoke-key-btn">Disable</button>
            <button class="btn-save" type="button" (click)="cancelRevokeKey()" [disabled]="saving">Cancel</button>
        </div>
    </div>
</div>
